import React, { useState, useMemo, useEffect } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import {
  Divider,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Paper,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Button,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { format } from 'date-fns';
import { WorkOrderDetails } from './WorkOrderDetails';
import { WorkOrder, SalesWorkOrdersOverviewProps } from './types';
import api from '@tyrio/api-factory';
import WorkOrderSearchInput from './components/WorkOrderSearchInput';
import DateTimeRangePicker from '../purchase-orders/components/DateTimeRangePicker';
import WorkOrderBranchSelector from './components/WorkOrderBranchSelector';
import WorkOrderStatusSelector from './components/WorkOrderStatusSelector';
import styled from '@emotion/styled';
import { tyrioUI } from '@tyrio/ui-library';
import StyledPagination from '../../components/Pagination/Pagination';
import NoDataFound from '../../components/NoData/NoDataFound';
import {
  LoaderWrapper,
  PageTemplateWrapper,
} from '../../components/PageTemplate/PageTemplate';
import _ from 'lodash';
import { startCase } from 'lodash';
import WorkOrderTableSkeleton from './components/WorkOrderTableSkeleton';
import { ToastHelper, ToastType } from '@tyrio/ui-library';
import { useAuth } from '../../context/AuthContext';
import { ClientSettings } from '@tyrio/dto';

// Enum for table row statuses (matching transfer orders)
export enum SelectedProductStatuses {
  FAVORITE,
  SELECTED,
  NORMAL,
}

// Styled components matching transfer orders exactly
const Container = styled.div<{
  selectedOrder: WorkOrder | null;
  open: boolean;
}>`
  background: #fff;
  display: flex;
  flex-direction: column;
  height: 100%;
  border-radius: 8px;
  width: ${(prop) =>
    prop.selectedOrder === null ? '100%' : `calc(100% - 816px)`};
  transition: linear 0.4s;
  position: relative;
  left: 0;
  height: calc(100svh - 44px);
`;

const WorkOrderDetailsWrapper = styled.div`
  min-width: 800px;
  max-width: 800px;
  width: 100%;
  margin-left: 16px;
  position: relative;
  right: 0;
  height: calc(100svh - 44px);
`;

const HeaderWrapperTop = styled.div`
  display: flex;
  padding: 16px;
  align-items: center;
  justify-content: space-between;
`;

export const HeaderText = styled.span`
  font-weight: 700;
  font-size: 24px;
  line-height: 32px;
`;

const RefreshButton = styled(Button)`
  width: 200px;
  height: 40px;
  white-space: nowrap;
`;

// Styled components matching transfer orders
const StyledTable = styled(Table)`
  white-space: nowrap;
`;

const StyledTableHead = styled(TableHead)`
  position: sticky;
  top: 0;
  z-index: 2;
`;

const StyledTableRowHeader = styled(TableRow)`
  background: #ecf0f4;
`;

const StyledTableRow = styled(TableRow)<{
  status: SelectedProductStatuses;
}>`
  height: 60px;
  background: ${(props) =>
    props.status === SelectedProductStatuses.SELECTED
      ? tyrioUI.colors.blue.B10
      : props.status === SelectedProductStatuses.FAVORITE
      ? tyrioUI.colors.yellow.B10
      : 'white'};

  &:hover {
    background: ${tyrioUI.colors.black.B10};
    border-bottom: 1.5px solid ${tyrioUI.colors.black.B40};
    cursor: pointer;
  }
`;

const StyledTableCell = styled(TableCell)<{
  status: SelectedProductStatuses;
  opacity?: boolean;
}>`
  border-bottom: 1px solid
    ${(props) =>
      props.status === SelectedProductStatuses.SELECTED
        ? tyrioUI.colors.blue.B100
        : props.status === SelectedProductStatuses.FAVORITE
        ? tyrioUI.colors.yellow.B100
        : tyrioUI.colors.black.B40};
  opacity: ${(props) => (props.opacity ? '0.3' : '1')};
`;

const CellDetails = styled.div`
  font-weight: 400;
  font-size: 12px;
  color: #919eab;
`;

const CenterStatus = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
`;

const OrderStatus = styled.div<{
  color: string;
}>`
  width: fit-content;
  border-radius: 100px;
  padding: 5px;
  padding-left: 7px;
  padding-right: 7px;
  color: ${(props) => (props.color !== '#FFFFFF' ? 'white' : 'gray')};
  font-weight: 500;
  font-size: 12px;
  line-height: 24px;
  display: flex;
  align-items: center;
  height: 24px;
  background: ${(prop) => prop.color};
  border: ${(props) =>
    props.color !== '#FFFFFF' ? 'none' : '1px solid #DFE3E8'};
`;

const SubStatus = styled.p`
  margin: 5px 0 0 0;
  font-size: 12px;
  color: gray;
`;

// Type definitions for paginated response
interface PaginatedWorkOrdersResponse {
  data: any[];
  pagination: {
    page: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
  };
}

// Add the refresh functionality
const useRefreshWorkOrders = () => {
  const { user } = useAuth();
  const [isRefreshing, setIsRefreshing] = useState(false);

  const refreshWorkOrders = async (
    refetchData: () => void,
    queryParams: {
      searchType?: string;
      searchKeyword?: string;
      startDate?: Date | null;
      endDate?: Date | null;
      branchId?: string | null;
      orderStatus?: string | null;
    },
    onRefreshStart?: () => void
  ) => {
    const settings = user?.client?.generalSettings as unknown as ClientSettings;

    if (!settings?.ritamApiKey) {
      ToastHelper.showToast(
        '',
        ToastType.ERROR,
        undefined,
        'Ritam API key is not configured'
      );
      return;
    }

    if (onRefreshStart) {
      onRefreshStart();
    }

    setIsRefreshing(true);

    try {
      // Fetch work orders matching current query parameters using pagination
      const workOrdersResponse = (await api.fetch('get_work_orders_paginated', {
        page: '1',
        pageSize: '250',
        searchType: queryParams.searchKeyword ? queryParams.searchType : undefined,
        searchKeyword: queryParams.searchKeyword || undefined,
        startDate: queryParams.startDate ? queryParams.startDate.toISOString() : undefined,
        endDate: queryParams.endDate ? queryParams.endDate.toISOString() : undefined,
        excludeCompleted: 'true',
        excludeCancelled: 'true',
        requireErpId: 'true',
        branchId: queryParams.branchId || undefined,
        orderStatus: queryParams.orderStatus || undefined,
      })) as PaginatedWorkOrdersResponse;

      const workOrdersWithErpId = workOrdersResponse.data.filter(
        (order) => order.erpOrderId && order.erpOrderId !== 'N/A'
      );

      if (workOrdersWithErpId.length === 0) {
        ToastHelper.showToast(
          '',
          ToastType.WARNING,
          undefined,
          'No work orders with ERP ID found'
        );
        return;
      }

      const erpIds = workOrdersWithErpId.map((order) => order.erpOrderId);
      const apiUrl = `https://ritam.tyreapi.com/api/aa/workorders/by-ids?ids=${erpIds.join(
        ','
      )}&status=`;

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'x-api-key': settings.ritamApiKey,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const ritamData = await response.json();
      let updatedCount = 0;
      const updates = [];

      for (const workOrder of workOrdersWithErpId) {
        const ritamOrder = ritamData.find(
          (item: any) => item.id == workOrder.erpOrderId
        );

        if (ritamOrder) {
          let newStatus = null;
          switch (ritamOrder.status) {
            case 'N':
              newStatus = 'NEW';
              break;
            case 'P':
              newStatus = 'CONFIRMED';
              break;
            case 'D':
              newStatus = 'COMPLETED';
              break;
            case 'S':
              newStatus = 'CANCELLED';
              break;
          }

          if (newStatus && newStatus !== workOrder.externalStatus) {
            updates.push({
              workOrderId: workOrder.id,
              newStatus,
              oldStatus: workOrder.externalStatus,
            });
            updatedCount++;
          }
        }
      }

      if (updates.length > 0) {
        for (const update of updates) {
          try {
            await api.fetch('update_work_order', {
              id: update.workOrderId,
              externalStatus: update.newStatus,
            });
          } catch (updateError) {
            console.error(
              `Failed to update work order ${update.workOrderId}:`,
              updateError
            );
          }
        }
      }

      await refetchData();

      ToastHelper.showToast(
        '',
        ToastType.SUCCESS,
        undefined,
        `Refresh complete! Updated ${updatedCount} of ${workOrdersWithErpId.length} work orders.`
      );
    } catch (error) {
      console.error('Error refreshing work orders:', error);
      let errorMessage = 'Failed to refresh work orders.';
      
      if (error instanceof Error) {
        if (error.message.includes('HTTP error')) {
          errorMessage = 'Failed to connect to Ritam API. Please check your API key and try again.';
        } else if (error.message.includes('update_work_order')) {
          errorMessage = 'Failed to update work orders in the database. Please try again.';
        }
      }
      
      ToastHelper.showToast(
        '',
        ToastType.ERROR,
        undefined,
        errorMessage
      );
    } finally {
      setIsRefreshing(false);
    }
  };

  return { refreshWorkOrders, isRefreshing };
};

export const SalesWorkOrdersOverview: React.FC<
  SalesWorkOrdersOverviewProps
> = ({ sidebarOpen }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [selectedWorkOrder, setSelectedWorkOrder] = useState<WorkOrder | null>(
    null
  );
  const [searchType, setSearchType] = useState<string>('workOrderNumber');
  const [searchKeyword, setSearchKeyword] = useState<string>('');
  const [dateFilters, setDateFilters] = useState({
    startDate: (() => {
      const date = new Date();
      date.setDate(date.getDate() - 30);
      date.setHours(0, 0, 0, 0);
      return date;
    })(),
    endDate: (() => {
      const date = new Date();
      date.setHours(23, 59, 59, 999);
      return date;
    })(),
  });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [branchId, setBranchId] = useState<string | null>(null);
  const [orderStatus, setOrderStatus] = useState<string | null>(null);

  const { refreshWorkOrders, isRefreshing } = useRefreshWorkOrders();

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [searchType, searchKeyword, dateFilters.startDate, dateFilters.endDate, branchId, orderStatus]);

  // Fetch work orders from API with server-side pagination
  const {
    data: workOrdersResponse,
    isLoading,
    error,
    refetch,
  } = useQuery(
    [
      'get_work_orders_paginated',
      page,
      pageSize,
      searchType,
      searchKeyword,
      dateFilters.startDate,
      dateFilters.endDate,
      branchId,
      orderStatus,
    ],
    async () => {
      const response = await api.fetch('get_work_orders_paginated', {
        page: page.toString(),
        pageSize: pageSize.toString(),
        searchType: searchKeyword ? searchType : undefined,
        searchKeyword: searchKeyword || undefined,
        startDate: dateFilters.startDate ? dateFilters.startDate.toISOString() : undefined,
        endDate: dateFilters.endDate ? dateFilters.endDate.toISOString() : undefined,
        excludeCompleted: 'true',
        excludeCancelled: 'true',
        requireErpId: 'true',
        branchId: branchId || undefined,
        orderStatus: orderStatus || undefined,
      });
      return response as PaginatedWorkOrdersResponse;
    },
    {
      refetchOnWindowFocus: true,
      keepPreviousData: true, // Keep previous data while loading new page
    }
  );

  const workOrdersData = workOrdersResponse?.data || [];
  const pagination = workOrdersResponse?.pagination || {
    totalCount: 0,
    totalPages: 0,
  };

  // Transform work orders data for display
  const transformedWorkOrders = useMemo(() => {
    if (!workOrdersData) return [];

    return workOrdersData.map((order: any) => {
      // Customer name logic matching Customer Orders table
      const customerPayload = order.customerOrder?.customer;
      const customerFirstNameLastName = `${customerPayload?.firstName ?? ''} ${
        customerPayload?.lastName ?? ''
      }`.trim();

      const customerCompanyInfo = customerPayload?.companyInfo;

      const customerTitle =
        customerCompanyInfo && customerCompanyInfo?.companyName !== ''
          ? customerCompanyInfo?.companyName
          : customerFirstNameLastName || 'Unknown';

      const customerSubTitle =
        customerCompanyInfo &&
        customerCompanyInfo?.companyName !== '' &&
        customerFirstNameLastName;

      // Delivery logic matching Customer Orders table
      const deliveryAddress = order.customerOrder?.deliveryAddress;
      const delivery =
        order.customerOrder?.delivery?.code === 'FIT'
          ? `${deliveryAddress?.city} - ${deliveryAddress?.companyInfo?.companyName}`
          : deliveryAddress?.city;

      return {
        id: order.id,
        workOrderNumber: order.orderNumber || 'N/A',
        erpId: order.erpOrderId || 'N/A',
        erpDocNumber: order.erpDocNumber || '',
        customerOrderNumber:
          _.get(order.customerOrder, 'details.number') || 'N/A',
        customerOrderStatus:
          order.customerOrder?.orderStatus?.status || 'Unknown',
        branchName: order.branch?.branchName || 'N/A',
        paymentMethodName: order.customerOrder?.payment?.name || 'Unknown',
        paymentStatus: order.customerOrder?.paymentStatus || 'Unknown',
        deliveryMethodName: order.customerOrder?.delivery?.name || 'Unknown',
        deliveryDetails: delivery || '',
        externalStatus:
          order.externalStatus || order.orderStatus?.status || 'Unknown',
        createdAt: order.createdAt,
        updatedAt: order.updatedAt,
        customerTitle,
        customerSubTitle,
        totalAmount: order.customerOrder?.lineItems?.reduce(
          (sum: number, item: any) => sum + item.price * item.qty,
          0
        ),
        customerRemark: order.customerOrder?.customerRemark || '',
        internalRemark: order.customerOrder?.internalRemark || '',
        rawData: order,
      };
    });
  }, [workOrdersData]);

  const getStatusChipColor = (status: string): string => {
    switch (status?.toUpperCase()) {
      case 'PENDING':
      case 'NEW':
        return '#FFCC4D';
      case 'IN_PROGRESS':
      case 'PROCESSING':
        return '#1976D2';
      case 'SHIPPED':
      case 'CONFIRMED':
        return '#38A169';
      case 'PAID':
        return '#38A169';
      case 'UNPAID':
      case 'PENDING_PAYMENT':
        return '#FFCC4D';
      case 'CANCELLED':
      case 'FAILED':
        return '#F56565';
      default:
        return '#919EAB';
    }
  };

  const getPaymentStatusColor = (status: string): string => {
    switch (status?.toUpperCase()) {
      case 'PAID':
        return '#38A169';
      case 'NOT_PAID':
        return '#DB1E1E';
      case 'PENDING':
        return '#FFCC4D';
      default:
        return '#919EAB';
    }
  };

  const handleRowClick = (workOrder: WorkOrder) => {
    setSelectedWorkOrder(workOrder);
  };

  const handleDateFiltersChange = (filters: any) => {
    setDateFilters({
      startDate: filters.startDate,
      endDate: filters.endDate,
    });
  };

  const handleChangePageSize = (event: any) => {
    setPageSize(Number(event.target.value));
    setPage(1);
  };

  const handleRefreshClick = async () => {
    // Create a custom refetch function that properly invalidates the cache
    const customRefetch = async () => {
      // Invalidate and refetch all work orders queries
      await queryClient.invalidateQueries(['get_work_orders_paginated']);
      await queryClient.invalidateQueries(['get_work_orders']);
      await queryClient.invalidateQueries(['get_work_orders_with_branch_info']);

      // Force refetch the current query
      await refetch();
    };

    await refreshWorkOrders(
      customRefetch,
      {
        searchType,
        searchKeyword,
        startDate: dateFilters.startDate,
        endDate: dateFilters.endDate,
        branchId,
        orderStatus
      },
      () => setSelectedWorkOrder(null)
    );
  };

  return (
    <PageTemplateWrapper
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        right: 0,
        position: 'absolute',
      }}
    >
      <Container selectedOrder={selectedWorkOrder} open={sidebarOpen}>
        <HeaderWrapperTop>
          <HeaderText>Work Orders</HeaderText>
          <RefreshButton
            variant="contained"
            color="info"
            disabled={isLoading || isRefreshing}
            onClick={handleRefreshClick}
            disableElevation
            startIcon={
              isLoading || isRefreshing ? (
                <CircularProgress
                  style={{ height: '16px', width: '16px' }}
                  sx={{ color: 'white' }}
                />
              ) : (
                <RefreshIcon />
              )
            }
          >
            Refresh Work Orders
          </RefreshButton>
        </HeaderWrapperTop>
        <Divider />

        {/* Filters Header */}
        <Grid
          container
          spacing={1.5}
          sx={{
            width: '100%',
            padding: '16px 16px 0 16px',
          }}
        >
          <Grid item xs={3}>
            <WorkOrderSearchInput
              searchType={searchType}
              searchKeyword={searchKeyword}
              onSearchTypeChange={setSearchType}
              onSearchKeywordChange={setSearchKeyword}
            />
          </Grid>
          <Grid item xs={3}>
            <DateTimeRangePicker
              isSidebarOpen={!!selectedWorkOrder}
              filters={dateFilters}
              setFilters={handleDateFiltersChange}
            />
          </Grid>
          <Grid item xs={3}>
            <WorkOrderBranchSelector
              selectedBranchId={branchId}
              onBranchChange={(id) => setBranchId(id)}
            />
          </Grid>
          <Grid item xs={3}>
            <WorkOrderStatusSelector
              selectedStatus={orderStatus}
              onStatusChange={(status) => setOrderStatus(status)}
            />
          </Grid>
        </Grid>

        {isLoading ? (
          <WorkOrderTableSkeleton />
        ) : error ? (
          <Box sx={{ padding: '16px' }}>
            <Alert severity="error">
              {String(
                t('sales.workOrders.errorLoading') ||
                  'Error loading work orders. Please try again.'
              )}
            </Alert>
          </Box>
        ) : workOrdersData && workOrdersData.length > 0 ? (
          <>
            <TableContainer
              component={Paper}
              sx={{ marginTop: '32px', borderRadius: 0 }}
            >
              <StyledTable aria-label="work orders table">
                <StyledTableHead>
                  <StyledTableRowHeader>
                    <TableCell sx={{ textAlign: 'center' }}>Status</TableCell>
                    <TableCell>Work Order</TableCell>
                    <TableCell>ERP ID</TableCell>
                    <TableCell>Customer Order</TableCell>
                    <TableCell>Customer</TableCell>
                    <TableCell>Branch</TableCell>
                    <TableCell>Payment</TableCell>
                    <TableCell>Delivery</TableCell>
                    <TableCell>Note</TableCell>
                  </StyledTableRowHeader>
                </StyledTableHead>
                <TableBody>
                  {transformedWorkOrders.map((order: any) => {
                    const getTableRowColor = () => {
                      if (selectedWorkOrder?.id === order.id) return '#E4F5FE';
                      return '#fff';
                    };

                    const status =
                      selectedWorkOrder?.id === order.id
                        ? SelectedProductStatuses.SELECTED
                        : SelectedProductStatuses.NORMAL;

                    return (
                      <StyledTableRow
                        key={order.id}
                        sx={{
                          background: getTableRowColor(),
                          position: 'relative',
                        }}
                        onClick={() => handleRowClick(order)}
                        status={status}
                      >
                        <StyledTableCell status={status}>
                          <CenterStatus>
                            <OrderStatus
                              color={getStatusChipColor(order.externalStatus)}
                            >
                              {order.externalStatus}
                            </OrderStatus>
                          </CenterStatus>
                        </StyledTableCell>
                        <StyledTableCell status={status}>
                          {order.workOrderNumber}
                          <CellDetails>
                            {format(new Date(order.createdAt), 'dd.MM - HH:mm')}
                          </CellDetails>
                        </StyledTableCell>
                        <StyledTableCell status={status}>
                          {order.erpDocNumber || order.erpId}
                          <CellDetails>
                            {order.erpDocNumber ? order.erpId : ''}
                          </CellDetails>
                        </StyledTableCell>
                        <StyledTableCell status={status}>
                          {order.customerOrderNumber}
                          <CellDetails
                            style={{
                              color: getStatusChipColor(
                                order.customerOrderStatus
                              ),
                            }}
                          >
                            {order.customerOrderStatus}
                          </CellDetails>
                        </StyledTableCell>
                        <StyledTableCell status={status}>
                          {order.customerTitle}
                          {order.customerSubTitle && (
                            <CellDetails>{order.customerSubTitle}</CellDetails>
                          )}
                        </StyledTableCell>
                        <StyledTableCell status={status}>
                          {order.branchName}
                        </StyledTableCell>
                        <StyledTableCell status={status}>
                          {order.paymentMethodName}
                          <CellDetails
                            style={{
                              color: getPaymentStatusColor(order.paymentStatus),
                            }}
                          >
                            {startCase(order.paymentStatus)}
                          </CellDetails>
                        </StyledTableCell>
                        <StyledTableCell status={status}>
                          {order.deliveryMethodName}
                          {order.deliveryDetails && (
                            <CellDetails>{order.deliveryDetails}</CellDetails>
                          )}
                        </StyledTableCell>
                        <StyledTableCell status={status}>
                          {order.customerRemark}
                          <CellDetails style={{ color: 'red' }}>
                            {order.internalRemark}
                          </CellDetails>
                        </StyledTableCell>
                      </StyledTableRow>
                    );
                  })}
                </TableBody>
              </StyledTable>
            </TableContainer>
            <StyledPagination
              totalPageNumber={pagination.totalPages}
              page={page}
              setPage={setPage}
              selectedOrderData={selectedWorkOrder}
              pageSize={pageSize}
              handleChangePageSize={handleChangePageSize}
              menuItems={[20, 50, 100, 200]}
            />
          </>
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '400px',
              textAlign: 'center',
              color: '#666',
            }}
          >
            <Typography variant="h6" gutterBottom>
              {t('No work orders found')}
            </Typography>
            <Typography variant="body2">
              {t('Please try adjusting the filters')}
            </Typography>
          </Box>
        )}
      </Container>

      {/* Details Panel - matching transfer orders exactly */}
      {selectedWorkOrder && (
        <WorkOrderDetailsWrapper>
          <WorkOrderDetails
            workOrder={selectedWorkOrder}
            onClose={() => setSelectedWorkOrder(null)}
            onSave={refetch}
          />
        </WorkOrderDetailsWrapper>
      )}
    </PageTemplateWrapper>
  );
};

export default SalesWorkOrdersOverview;
